import axios from 'axios'

import apiService from './ApiService'
import Token from '../models/auth/Token'
import JWT from './JWT'
import config from './../config'

const LOGIN_EVENT = 'login'
const LOGOUT_EVENT = 'logout'

const {
  SITE_URL,
  AUTH_CLIENT_ID,
  AUTH_CLIENT_SECRET,
  AUTH_CLIENT_REDIRECT_URI
} = config.env

export const GRANT = {
  ORIGIN: 'origin',
  CLIENT_CREDENTIALS: 'client_credentials',
  AUTHORIZATION_CODE: 'authorization_code',
  REFRESH_TOKEN: 'refresh_token',
  PASSWORD: 'password'
}

const MAX_WATCH_DOG_INTERVALS = 60

class AuthService {
  constructor () {
    this.token = null
    this.isRefreshing = false
  }

  setToken (token) {
    this.token = token
    if (token) {
      localStorage.setItem('token', Token.toJson(token))
    } else {
      localStorage.removeItem('token')
    }
  }

  getToken () {
    return this.token
  }

  decodeAccessToken () {
    if (!this.token || !this.token.access_token) {
      return null
    }

    return JWT.decode(this.token.access_token)
  }

  isAuthenticated () {
    return this.token !== null
  }

  triggerLoggedInEvent () {
    document.body.dispatchEvent(new Event(LOGIN_EVENT))
  }

  triggerLogoutEvent () {
    document.body.dispatchEvent(new Event(LOGOUT_EVENT))
  }

  registerLoginHandler (callback) {
    document.body.addEventListener(LOGIN_EVENT, callback, false)
  }

  registerLogoutHandler (callback) {
    document.body.addEventListener(LOGOUT_EVENT, callback, false)
  }

  logout () {
    this.setToken(null)
    localStorage.removeItem('token')
    this.triggerLogoutEvent()
  }

  getTokenFromLocalStorage () {
    const token = localStorage.getItem('token')

    if (!token) {
      return null
    }

    this.setToken(Token.fromJson(token))
  }

  async refreshToken () {
    this.isRefreshing = true

    const { refresh_token, grant_type } = this.token
    const newToken = await this
      .doTokenRequest(GRANT.REFRESH_TOKEN, { refresh_token })
      .catch((error) => {
        return null
      })

    if (newToken === null) {
      if (grant_type === GRANT.AUTHORIZATION_CODE) {
        await this.loginByAuthorization()
      } else {
        this.setToken(null)
        return null
      }
    }

    return newToken
  }

  loginByPassword (username, password) {
    return new Promise((resolve, reject) => {
      this.doTokenRequest(GRANT.PASSWORD, { username, password })
        .then((response) => {
          resolve(response)
        })
        .catch(reject)
    })
  }

  getCodeFromUrl (url = null) {
    url = new URL(url || window.location.href)

    return url.searchParams.get('code')
  }

  loginByAuthorization () {
    const params = {
      response_type: 'code',
      client_id: AUTH_CLIENT_ID,
      redirect_uri: AUTH_CLIENT_REDIRECT_URI,
      scope: []
    }

    return new Promise((resolve, reject) => {
      const authorizeUrl = SITE_URL + '/authorize'

      if (new URL(authorizeUrl).origin === window.location.origin) {
        axios.get(authorizeUrl, { params }).then((response) => {
          const code = this.getCodeFromUrl(response.request.responseURL)

          if (code === null) {
            window.location.href = authorizeUrl + '?' + new URLSearchParams(params)
            return
          }

          this.decodeCodeToToken(code).then(resolve).catch(reject)
        }).catch(reject)
      } else {
        window.location.href = authorizeUrl + '?' + new URLSearchParams(params)
        return
      }
    })
  }

  loginByOrigin () {
    const options = {
      useClientSecret: false
    }

    return this.doTokenRequest('origin', { }, options)
  }

  async decodeCodeToToken (code = null) {
    if (code === null) {
      code = this.getCodeFromUrl()
    }

    if (code !== null) {
      return await this.doTokenRequest(GRANT.AUTHORIZATION_CODE, {
        code,
        redirect_uri: AUTH_CLIENT_REDIRECT_URI
      })
    } else {
      return Promise.reject('Code not defined')
    }
  }

  async waitForTokenRefresh () {
    return await new Promise((resolve, reject) => {
      let watchDogCounter = 0
      const watchDogInterval = setInterval(() => {
        watchDogCounter++
        if (this.isRefreshing === false) {
          clearInterval(watchDogInterval)
          resolve(this.token.access_token)
        }

        if (watchDogCounter >= MAX_WATCH_DOG_INTERVALS) {
          clearInterval(watchDogInterval)
          reject('Refresh failed')
        }
      }, 1000)
    })
  }

  /**
   * @returns {Promise<null>}
   */
  async getAccessToken () {
    if (this.token === null) {
      this.getTokenFromLocalStorage()
    }

    if (this.token === null || this.token.access_token === null) {
      return null
    }

    if (this.isRefreshing) {
      return await this.waitForTokenRefresh()
    }

    if (this.token.isExpired()) {
      const accessToken = await this.refreshToken()

      if (accessToken === null) {
        return null
      }
    }

    return this.token.access_token
  }

  async doTokenRequest (type, data, options = {}) {
    const formData = new FormData()

    options = {
      useClientSecret: true,
      ...options
    }

    formData.set('grant_type', type)
    formData.set('client_id', AUTH_CLIENT_ID)

    if (options.useClientSecret === true) {
      formData.set('client_secret', AUTH_CLIENT_SECRET)
    }

    for (const key of Object.keys(data)) {
      formData.set(key, data[key])
    }

    return await new Promise((resolve, reject) => {
      this.setToken(null)
      apiService.post('/token', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then((result) => {
        this.setToken(new Token({
          ...result.data,
          grant_type: type
        }))
        this.isRefreshing = false
        this.triggerLoggedInEvent()
        resolve(result)
      }).catch(error => {
        this.setToken(null)
        this.isRefreshing = false
        reject(error.response)
      })
    })
  }
}

export default new AuthService()
